import { colors } from "@/components/colors"
import { MetricThreshold, ThresholdRule } from "@/types/threshold"

/**
 * 주어진 값에 대해 첫 번째 매칭되는 임계값 룰을 반환
 */
export function evaluateThreshold(value: number, rules: ThresholdRule[]): ThresholdRule | null {
  // 더 구체적인 조건이 먼저 매칭되도록 정렬
  // > / >= : 값이 큰 룰부터 (150이상을 100이상보다 먼저 체크)
  // < / <= : 값이 작은 룰부터 (50미만을 100미만보다 먼저 체크)
  // == / between : 원래 순서 유지
  const sorted = [...rules].sort((a, b) => {
    const isAsc = (op: string) => op === ">" || op === ">="
    const isDesc = (op: string) => op === "<" || op === "<="

    if (isAsc(a.operator) && isAsc(b.operator)) return b.value - a.value
    if (isDesc(a.operator) && isDesc(b.operator)) return a.value - b.value

    return 0
  })

  for (const rule of sorted) {
    switch (rule.operator) {
      case ">":
        if (value > rule.value) return rule
        break
      case ">=":
        if (value >= rule.value) return rule
        break
      case "<":
        if (value < rule.value) return rule
        break
      case "<=":
        if (value <= rule.value) return rule
        break
      case "==":
        if (value === rule.value) return rule
        break
      case "between":
        if (rule.valueTo !== undefined && value >= rule.value && value <= rule.valueTo) return rule
        break
    }
  }

  return null
}

/**
 * 배경색의 밝기를 기반으로 텍스트 색상을 자동 결정
 */
function getContrastTextColor(bgHex: string): string {
  const hex = bgHex.replace("#", "")
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  // YIQ formula
  const yiq = (r * 299 + g * 587 + b * 114) / 1000

  return yiq >= 150 ? colors.neutral.grey["1000"] : colors.neutral.white
}

/**
 * 셀 값과 임계값 설정을 기반으로 스타일 객체를 반환
 */
export function getThresholdCellStyle(
  value: number | string | null | undefined,
  threshold?: MetricThreshold,
): { backgroundColor: string; color: string } | null {
  if (!threshold || !threshold.enabled || !threshold.rules.length) {
    return null
  }

  if (value === null || value === undefined || value === "" || value === "-") {
    return null
  }

  let numValue: number
  if (typeof value === "number") {
    numValue = value
  } else {
    // locale string to number (e.g. "1,234.5" → 1234.5)
    numValue = parseFloat(String(value).replace(/,/g, ""))
  }

  if (isNaN(numValue)) {
    return null
  }

  const matchedRule = evaluateThreshold(numValue, threshold.rules)
  if (!matchedRule) {
    return null
  }

  return {
    backgroundColor: matchedRule.color,
    color: matchedRule.textColor || getContrastTextColor(matchedRule.color),
  }
}
